import { useState, useEffect, useRef } from "react";
import { fetchPVROptions } from "../services/PVRservice";
import { getPartAndSubPart } from "../config/defectConfig";
import { createDefects } from "../services/Elv3Service";

export const useDefectCreation = ({
  devices,
  elv3Guid,
  initialDefect = null,
}) => {
  const [defects, setDefects] = useState({});
  const [devicePartOptions, setDevicePartOptions] = useState([]);
  const [violatingConditionOptions, setViolatingConditionOptions] = useState(
    []
  );
  const [suggestedRemedyOptions, setSuggestedRemedyOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [error, setError] = useState(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchPVROptions();
        if (!response) {
          throw new Error("Failed to fetch violation options");
        }
        setDevicePartOptions(response.deviceParts || []);
        setViolatingConditionOptions(response.violatingConditions || []);
        setSuggestedRemedyOptions(response.suggestedRemedies || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDropdownOptions();
  }, []);

  // Initialize defects structure when devices change or when elv3Guid is set
  useEffect(() => {
    // Only initialize once to prevent infinite loops
    if (initializedRef.current) {
      return;
    }

    if (devices && Array.isArray(devices) && devices.length > 0) {
      console.log("Initializing defects structure with devices:", devices);
      console.log("Using ELV3 GUID:", elv3Guid);

      // Create an object with keys for each device ID and empty arrays as values
      const initialDefects = devices.reduce((acc, device) => {
        const deviceId = device.DeviceID || device.deviceID;

        // If this device matches the initialDefect (if provided), add that defect
        if (initialDefect && initialDefect.deviceId === deviceId) {
          acc[deviceId] = [
            {
              elv3Guid: elv3Guid || initialDefect.elv3Guid || "",
              deviceId: deviceId,
              deviceTypeLabel: device.DeviceTypeLabel || device.deviceType,
              defectStatus: initialDefect.defectStatus || "1",
              elevatorPart:
                initialDefect.partNumber || initialDefect.elevatorPart || "",
              elevatorPartLabel:
                initialDefect.partName || initialDefect.elevatorPartLabel || "",
              violationCondition: initialDefect.violationCondition || "",
              violationConditionLabel:
                initialDefect.violationConditionLabel || "",
              suggestedRemedy: initialDefect.suggestedRemedy || "",
              suggestedRemedyLabel: initialDefect.suggestedRemedyLabel || "",
              comments: initialDefect.comments || "",
            },
          ];
        } else {
          // Otherwise just create an empty array for this device
          acc[deviceId] = [];
        }
        return acc;
      }, {});

      setDefects(initialDefects);
      initializedRef.current = true;
    }
  }, [devices, elv3Guid, initialDefect]);

  // Handle updates to elv3Guid when it changes after initialization
  useEffect(() => {
    if (!initializedRef.current || !elv3Guid) {
      return;
    }

    // Update all existing defects with the new elv3Guid
    setDefects((prev) => {
      const updatedDefects = {};
      for (const [deviceId, deviceDefects] of Object.entries(prev)) {
        updatedDefects[deviceId] = deviceDefects.map((defect) => ({
          ...defect,
          elv3Guid,
        }));
      }
      return updatedDefects;
    });
  }, [elv3Guid]);

  const handleAddDefect = (deviceId, deviceTypeLabel) => {
    setDefects((prev) => ({
      ...prev,
      [deviceId]: [
        ...(prev[deviceId] || []),
        {
          elv3Guid: elv3Guid || "",
          deviceId,
          deviceTypeLabel,
          defectStatus: "1",
          elevatorPart: "",
          elevatorPartLabel: "",
          violationCondition: "",
          violationConditionLabel: "",
          suggestedRemedy: "",
          suggestedRemedyLabel: "",
          comments: "",
        },
      ],
    }));
  };

  // Convert SSC defect to DOB defect
  const convertDefectToDOB = (defect) => {
    // The value in elevatorPart is actually the subpart (coming from the UI selection)
    // We need to map it to the correct part (category) using the getPartAndSubPart utility
    const mapping = getPartAndSubPart(parseInt(defect.elevatorPart));

    // If mapping is found, use the mapped partId and subPartId, otherwise fall back to original values
    const partNumber = mapping ? mapping.partId.toString() : "";
    const subPartNumber = mapping
      ? mapping.subPartId.toString()
      : defect.elevatorPart;

    console.log(
      `Converting defect: Part=${partNumber}, SubPart=${subPartNumber}`
    );

    return {
      ELV3Guid: defect.elv3Guid,
      DeviceID: defect.deviceId,
      DeviceTypeLabel: defect.deviceTypeLabel,
      DefectStatus: defect.defectStatus || "1",
      ElevatorPart: partNumber, // Main category (1-7)
      ElevatorSubPart: subPartNumber, // Actual selected part (1-100)
      ViolationCondition: defect.violationCondition,
      SuggestedRemedy: defect.suggestedRemedy,
      Comments: defect.comments || "",
    };
  };

  const handleRemoveDefect = (deviceId, index) => {
    setDefects((prev) => ({
      ...prev,
      [deviceId]: prev[deviceId].filter((_, i) => i !== index),
    }));
  };

  const handleDefectChange = (deviceId, index, field, value) => {
    setDefects((prev) => ({
      ...prev,
      [deviceId]: (prev[deviceId] || []).map((defect, i) => {
        if (i === index) {
          const updates = { [field]: value };
          const optionsMap = {
            elevatorPart: {
              options: devicePartOptions,
              idField: "partNumber",
              valueField: "partNumber",
              labelKey: "partName",
              labelField: "elevatorPartLabel",
            },
            violationCondition: {
              options: violatingConditionOptions,
              idField: "id",
              valueField: "vcLetter",
              labelKey: "vcName",
              labelField: "violationConditionLabel",
            },
            suggestedRemedy: {
              options: suggestedRemedyOptions,
              idField: "id",
              valueField: "srNumber",
              labelKey: "srName",
              labelField: "suggestedRemedyLabel",
            },
          };

          if (field in optionsMap) {
            const { options, idField, labelKey, labelField } =
              optionsMap[field];
            const option = options.find(
              (opt) => opt[idField].toString() === value.toString()
            );
            if (option) {
              updates[field] = option[idField].toString();
              updates[labelField] = option[labelKey];
            } else {
              updates[labelField] = "";
            }
          }

          return { ...defect, ...updates };
        }
        return defect;
      }),
    }));
  };

  // Submit all defects to the backend
  const submitDefects = async (headers) => {
    try {
      setSubmissionStatus({
        status: "submitting",
        message: "Submitting defects...",
      });

      if (!elv3Guid) {
        throw new Error("Missing ELV3 Group GUID. Cannot submit defects.");
      }

      // 1. flatten & filter only fully‑filled defects
      const rawDefects = Object.values(defects).flat();
      const validDefects = rawDefects.filter(
        (d) => d.elevatorPart && d.violationCondition && d.suggestedRemedy
      );
      if (validDefects.length === 0) {
        throw new Error(
          "No valid defects to submit. Please fill all required fields."
        );
      }

      // 2. convert each one to the back‑end shape
      const payloads = validDefects.map(convertDefectToDOB);

      // 3. POST them one at a time (or Promise.all for parallel)
      const results = [];
      for (const defectPayload of payloads) {
        // logs will show each object exactly:
        console.log("About to send:", defectPayload);
        const res = await createDefects(defectPayload, headers);
        results.push(res);
      }

      setSubmissionStatus({
        status: "success",
        message: "Defects submitted successfully!",
        data: results,
      });

      return results;
    } catch (error) {
      setSubmissionStatus({
        status: "error",
        message: `Failed to submit defects: ${error.message}`,
      });
      throw error;
    }
  };

  const isValid = (defect) => {
    return (
      defect.elevatorPart && defect.violationCondition && defect.suggestedRemedy
    );
  };

  const hasValidDefects = () => {
    return Object.values(defects).some(
      (deviceDefects) => deviceDefects.length > 0 && deviceDefects.some(isValid)
    );
  };

  return {
    defects,
    devicePartOptions,
    violatingConditionOptions,
    suggestedRemedyOptions,
    isLoading,
    error,
    submissionStatus,
    handleAddDefect,
    handleRemoveDefect,
    handleDefectChange,
    submitDefects,
    convertDefectToDOB,
    isValid,
    hasValidDefects,
  };
};
